/**
 * Real WebGL 3D chess board (react-three-fiber).
 *
 * The babel-plugin shipped with `@emergentbase/visual-edits` injects
 * `x-line-number`, `x-file-name`, `x-component`, etc. props onto every
 * JSX element in source.  React-Three-Fiber's `applyProps` interprets
 * hyphenated prop names as nested-property paths (e.g. `position-x`
 * means `instance.position.x = …`) and crashes on `x-line-number`
 * because three.js objects don't have an `x` property.
 *
 * To stay compatible we author this entire 3D tree using
 * `React.createElement` directly — the visual-edits plugin only walks
 * JSX literals, so createElement calls go through untouched.
 *
 * Logical state is supplied by ChessRoom (board / selected / legalTo /
 * lastMove / checkSquare / flipped) and `onSquareClick` mirrors the
 * 2D board's contract so the existing WebSocket move pipeline keeps
 * working without any chess-side change.
 */
import { Suspense, useMemo, useRef, createElement as h } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { makeGrainTexture } from "@/lib/grainTexture";

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];
const ROLE_BY_TYPE = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };

// Per user iter 59: replace the painted chess-figure PNGs with the
// CLASSIC SIMPLISTIC CHESS-PIECE ICONS — flat-coloured silhouettes
// drawn from Unicode chess glyphs.  White pieces render as white
// fill with a dark stroke; black pieces render as black fill with a
// light stroke.  Texture is built once per (side, role) on demand
// via an HTMLCanvasElement → THREE.CanvasTexture, so no PNG asset
// fetch is needed.
const PIECE_GLYPH = {
  king:   "\u265A",
  queen:  "\u265B",
  rook:   "\u265C",
  bishop: "\u265D",
  knight: "\u265E",
  pawn:   "\u265F",
};
const PIECE_FONT_STACK =
  "'Segoe UI Symbol','Apple Symbols','Noto Sans Symbols 2','DejaVu Sans','Arial Unicode MS',sans-serif";

const _iconTextureCache = new Map();
function getChessIconTexture(side, role) {
  const key = `${side}-${role}`;
  if (_iconTextureCache.has(key)) return _iconTextureCache.get(key);
  // 4:6 aspect to match the existing plane proportions (W=0.78, H=1.18).
  const W = 256, H = 384;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `${Math.floor(W * 1.1)}px ${PIECE_FONT_STACK}`;
  const glyph = PIECE_GLYPH[role] || PIECE_GLYPH.pawn;
  // Draw the glyph slightly above-centre so the wider base sits
  // correctly within the texture frame.
  const cx = W / 2;
  const cy = H * 0.56;
  // Outer "halo" — lay down a thick stroke first as a clean outline.
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = 14;
  ctx.strokeStyle = side === "white" ? "#1A1815" : "#F2EBD9";
  ctx.strokeText(glyph, cx, cy);
  // Inner solid fill — the classic flat-colour silhouette.
  ctx.fillStyle = side === "white" ? "#F2EBD9" : "#0F0D0A";
  ctx.fillText(glyph, cx, cy);
  // A second thinner accent stroke restores crisp edges at the top
  // of the silhouette where the fill meets the outline.
  ctx.lineWidth = 2;
  ctx.strokeStyle = side === "white" ? "#222018" : "#0F0D0A";
  ctx.strokeText(glyph, cx, cy);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  _iconTextureCache.set(key, tex);
  return tex;
}

const PAL = {
  // Per user iter 63: dark squares + frame recoloured to LIGHT BROWN
  // (was deep walnut #5C3B22).  Light squares stay ivory.
  light: "#EFE3C2",
  dark:  "#A07A50",
  rim:   "#A07A50",
  rimGold: "#D9B477",
  selected:  "#E8B86A",
  legal:     "#E8DDB8",
  lastMove:  "#A88B43",
  check:     "#DC2626",
};

function squareToWorld(file, rank) {
  const fIdx = FILES.indexOf(file);
  const rIdx = rank - 1;
  return { x: fIdx - 3.5, z: 3.5 - rIdx };
}

// ------------------------------------------------------------------
//  Lights — neutral, even illumination so the alternating tiles
//  remain visually distinct (no warm wash).
// ------------------------------------------------------------------
function Lights() {
  return h("group", null,
    h("ambientLight", { intensity: 0.7 }),
    h("directionalLight", {
      position: [6, 12, 4],
      intensity: 0.95,
      castShadow: true,
      "shadow-mapSize": [1024, 1024],
      color: "#FFFFFF",
    }),
    h("directionalLight", { position: [-6, 8, -4], intensity: 0.35, color: "#FFFFFF" }),
  );
}

// ------------------------------------------------------------------
//  Stained-wood frame around the squares — walnut, with a subtle
//  procedural grain.  Per user iter 61, the frame's surface picks up
//  a noise map that multiplies into the base walnut colour, creating
//  visible wood-grain striations.
// ------------------------------------------------------------------
function BoardFrame() {
  const grain = useMemo(() => makeGrainTexture("walnut-frame", 8, 0.55), []);
  return h("group", { position: [0, -0.04, 0] },
    h("mesh", { receiveShadow: true },
      h("boxGeometry", { args: [9.2, 0.10, 9.2] }),
      h("meshStandardMaterial", {
        color: PAL.rim,
        map: grain,
        metalness: 0.05,
        roughness: 0.85,
      }),
    ),
  );
}

// ------------------------------------------------------------------
//  One square + optional highlight pad
// ------------------------------------------------------------------
function BoardSquare({ file, rank, isLight, isSelected, isLegal, isLastMove, isCheck, onClick }) {
  const { x, z } = squareToWorld(file, rank);
  // Per user iter 62: dark squares get a markedly stronger grain map.
  const darkGrain = useMemo(() => makeGrainTexture("walnut-square", 1, 0.55), []);
  let highlight = null;
  let highlightOpacity = 0.4;
  if (isSelected) { highlight = PAL.selected; highlightOpacity = 0.65; }
  else if (isCheck) { highlight = PAL.check; highlightOpacity = 0.55; }
  else if (isLegal) { highlight = PAL.legal; highlightOpacity = 0.4; }
  else if (isLastMove) { highlight = PAL.lastMove; highlightOpacity = 0.35; }

  const stop = (e) => { e.stopPropagation(); onClick(); };

  const children = [
    h("mesh", { key: "sq", receiveShadow: true, onClick: stop },
      h("boxGeometry", { args: [0.98, 0.06, 0.98] }),
      h("meshStandardMaterial", {
        color: isLight ? PAL.light : PAL.dark,
        map: isLight ? null : darkGrain,
        metalness: 0.05,
        roughness: 0.85,
      }),
    ),
  ];
  if (highlight) {
    children.push(
      h("mesh", { key: "hl", position: [0, 0.034, 0], onClick: stop },
        h("boxGeometry", { args: [0.96, 0.005, 0.96] }),
        h("meshBasicMaterial", { color: highlight, transparent: true, opacity: highlightOpacity }),
      ),
    );
  }
  return h("group", { position: [x, 0, z] }, ...children);
}

// ------------------------------------------------------------------
//  Piece materials — light "ivory" vs dark "ebony", with a gold accent
//  used for crown rings and finials.
// ------------------------------------------------------------------
// Iter 137: per-role "black" piece tints so the silhouettes read more
// distinctly at a glance.  Pawn/Bishop are the darkest; the rest get
// progressively warmer / lighter charcoal so the player can pick
// rooks vs knights vs queen/king apart even from across the room.
const BLACK_BODY_BY_ROLE = {
  pawn:   "#0F0D0A",
  bishop: "#15110D",
  rook:   "#1F1812",
  knight: "#2A2018",
  queen:  "#33271D",
  king:   "#3D2E22",
};
function pieceMaterials(side, role) {
  const body = side === "white"
    ? { color: "#E2D5AA", metalness: 0.15, roughness: 0.55 }
    : { color: BLACK_BODY_BY_ROLE[role] || "#1B1815", metalness: 0.18, roughness: 0.55 };
  const accent = { color: PAL.rimGold, metalness: 0.85, roughness: 0.32 };
  return { body, accent };
}

// ------------------------------------------------------------------
//  Sculpted 3D piece bodies — built from THREE primitives so every
//  piece reads as a real volumetric object (no more painted sprite).
//  All pieces share the same pedestal (cylinder + gold torus rim)
//  from `ChessPiece` and stack their body on top.
// ------------------------------------------------------------------
function PawnBody({ mat }) {
  return h("group", null,
    // collar
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.20, 0.24, 0.08, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // body
    h("mesh", { position: [0, 0.30, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.13, 0.20, 0.42, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // head
    h("mesh", { position: [0, 0.62, 0], castShadow: true },
      h("sphereGeometry", { args: [0.16, 24, 18] }),
      h("meshStandardMaterial", mat.body),
    ),
  );
}

function RookBody({ mat }) {
  // Battlements made via 4 small box notches around the top.
  const notches = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    notches.push(
      h("mesh", { key: `n${i}`, position: [Math.cos(a) * 0.18, 0.74, Math.sin(a) * 0.18], castShadow: true },
        h("boxGeometry", { args: [0.10, 0.10, 0.10] }),
        h("meshStandardMaterial", mat.body),
      ),
    );
  }
  return h("group", null,
    // collar
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.22, 0.26, 0.08, 20] }),
      h("meshStandardMaterial", mat.body),
    ),
    // body
    h("mesh", { position: [0, 0.34, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.18, 0.22, 0.52, 20] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown plate
    h("mesh", { position: [0, 0.64, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.24, 0.22, 0.08, 20] }),
      h("meshStandardMaterial", mat.body),
    ),
    ...notches,
  );
}

function KnightBody({ mat }) {
  // Stylised L-shaped horse head: thick neck + tilted muzzle box.
  return h("group", null,
    // collar
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.20, 0.24, 0.08, 20] }),
      h("meshStandardMaterial", mat.body),
    ),
    // neck (cylinder leaning slightly forward toward viewer +z)
    h("mesh", { position: [0, 0.30, -0.02], rotation: [0.18, 0, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.13, 0.19, 0.46, 18] }),
      h("meshStandardMaterial", mat.body),
    ),
    // muzzle (forward-leaning box)
    h("mesh", { position: [0, 0.58, 0.10], rotation: [0.55, 0, 0], castShadow: true },
      h("boxGeometry", { args: [0.22, 0.18, 0.34] }),
      h("meshStandardMaterial", mat.body),
    ),
    // ears
    h("mesh", { position: [-0.07, 0.74, -0.05], castShadow: true },
      h("coneGeometry", { args: [0.04, 0.10, 12] }),
      h("meshStandardMaterial", mat.body),
    ),
    h("mesh", { position: [0.07, 0.74, -0.05], castShadow: true },
      h("coneGeometry", { args: [0.04, 0.10, 12] }),
      h("meshStandardMaterial", mat.body),
    ),
  );
}

function BishopBody({ mat }) {
  return h("group", null,
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.20, 0.24, 0.08, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    h("mesh", { position: [0, 0.32, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.14, 0.20, 0.46, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // collar bead
    h("mesh", { position: [0, 0.58, 0], castShadow: true },
      h("sphereGeometry", { args: [0.17, 24, 16] }),
      h("meshStandardMaterial", mat.body),
    ),
    // mitre
    h("mesh", { position: [0, 0.80, 0], castShadow: true },
      h("coneGeometry", { args: [0.13, 0.28, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown finial
    h("mesh", { position: [0, 0.98, 0], castShadow: true },
      h("sphereGeometry", { args: [0.045, 12, 10] }),
      h("meshStandardMaterial", mat.accent),
    ),
  );
}

function QueenBody({ mat }) {
  // Crown ring approximated with a torus + 8 small finial spheres.
  const finials = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    finials.push(
      h("mesh", { key: `q${i}`, position: [Math.cos(a) * 0.18, 0.86, Math.sin(a) * 0.18], castShadow: true },
        h("sphereGeometry", { args: [0.04, 10, 8] }),
        h("meshStandardMaterial", mat.accent),
      ),
    );
  }
  return h("group", null,
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.22, 0.26, 0.08, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    h("mesh", { position: [0, 0.34, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.16, 0.22, 0.52, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown shoulder
    h("mesh", { position: [0, 0.66, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.22, 0.18, 0.10, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown ring (gold)
    h("mesh", { position: [0, 0.78, 0], rotation: [Math.PI / 2, 0, 0] },
      h("torusGeometry", { args: [0.18, 0.025, 10, 32] }),
      h("meshStandardMaterial", mat.accent),
    ),
    ...finials,
    // top jewel
    h("mesh", { position: [0, 0.96, 0], castShadow: true },
      h("sphereGeometry", { args: [0.06, 14, 12] }),
      h("meshStandardMaterial", mat.accent),
    ),
  );
}

function KingBody({ mat }) {
  return h("group", null,
    h("mesh", { position: [0, 0.04, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.22, 0.26, 0.08, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    h("mesh", { position: [0, 0.34, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.18, 0.22, 0.52, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown shoulder
    h("mesh", { position: [0, 0.68, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.24, 0.18, 0.12, 24] }),
      h("meshStandardMaterial", mat.body),
    ),
    // crown ring
    h("mesh", { position: [0, 0.82, 0], rotation: [Math.PI / 2, 0, 0] },
      h("torusGeometry", { args: [0.20, 0.028, 10, 32] }),
      h("meshStandardMaterial", mat.accent),
    ),
    // cross — vertical bar
    h("mesh", { position: [0, 1.02, 0], castShadow: true },
      h("boxGeometry", { args: [0.05, 0.22, 0.05] }),
      h("meshStandardMaterial", mat.accent),
    ),
    // cross — horizontal bar
    h("mesh", { position: [0, 1.02, 0], castShadow: true },
      h("boxGeometry", { args: [0.16, 0.05, 0.05] }),
      h("meshStandardMaterial", mat.accent),
    ),
  );
}

const PIECE_BODY_BY_ROLE = {
  pawn:   PawnBody,
  rook:   RookBody,
  knight: KnightBody,
  bishop: BishopBody,
  queen:  QueenBody,
  king:   KingBody,
};

// ------------------------------------------------------------------
//  One 3D piece — pedestal + sculpted volumetric body (per user iter
//  136).  No more flat texture sprite.  Knight is the only piece with
//  inherent forward orientation; we rotate it so it faces the
//  opposing rank.
// ------------------------------------------------------------------
function ChessPiece({ piece, x, z, onClick }) {
  const side = piece.color === "w" ? "white" : "black";
  const role = ROLE_BY_TYPE[piece.type];
  const mat = useMemo(() => pieceMaterials(side, role), [side, role]);
  const stop = (e) => { e.stopPropagation(); onClick(); };

  // Knight: face opposing rank.  White knight faces -Z (toward black);
  // black knight faces +Z (toward white).  In group-local space (which
  // already accounts for `flipped` via the outer wrapper) this maps
  // exactly to: white → π, black → 0.
  const yRot = role === "knight"
    ? (side === "white" ? Math.PI : 0)
    : 0;

  const Body = PIECE_BODY_BY_ROLE[role] || PawnBody;
  return h("group", { position: [x, 0, z], onClick: stop },
    // pedestal
    h("mesh", { position: [0, 0.06, 0], castShadow: true, receiveShadow: true },
      h("cylinderGeometry", { args: [0.30, 0.34, 0.12, 24] }),
      h("meshStandardMaterial", {
        color: side === "white" ? "#E2D5AA" : (BLACK_BODY_BY_ROLE[role] || "#1B1815"),
        metalness: 0.15,
        roughness: 0.7,
      }),
    ),
    h("mesh", { position: [0, 0.121, 0] },
      h("torusGeometry", { args: [0.30, 0.018, 12, 48] }),
      h("meshStandardMaterial", { color: PAL.rimGold, metalness: 0.85, roughness: 0.32 }),
    ),
    // sculpted 3D body, sat on top of the pedestal.
    h("group", { position: [0, 0.13, 0], rotation: [0, yRot, 0] },
      h(Body, { mat }),
    ),
  );
}

// ------------------------------------------------------------------
//  Imperative camera flip when local player is black
// ------------------------------------------------------------------
function CameraOrient({ flipped }) {
  const { camera } = useThree();
  const lastFlip = useRef(null);
  if (lastFlip.current !== flipped) {
    lastFlip.current = flipped;
    const z = flipped ? -8 : 8;
    camera.position.set(0, 7, z);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }
  return null;
}

// ------------------------------------------------------------------
//  Public component
// ------------------------------------------------------------------
export default function ChessBoard3D({
  board,
  flipped,
  selected,
  legalTo,
  lastMove,
  checkSquare,
  onSquareClick,
  onBackdropClick,
  height = 520,
}) {
  const legalSet = useMemo(() => new Set(legalTo || []), [legalTo]);

  const cells = useMemo(() => {
    const out = [];
    for (let rIdx = 0; rIdx < 8; rIdx++) {
      for (let fIdx = 0; fIdx < 8; fIdx++) {
        const rank = 8 - rIdx;
        const file = FILES[fIdx];
        const sq = `${file}${rank}`;
        const piece = board?.[rIdx]?.[fIdx] || null;
        const isLight = (rIdx + fIdx) % 2 === 0;
        const isSelected = selected === sq;
        const isLegal = legalSet.has(sq);
        const isLastMove = lastMove && (lastMove.from === sq || lastMove.to === sq);
        const isCheck = checkSquare === sq;
        out.push({ sq, file, rank, piece, isLight, isSelected, isLegal, isLastMove, isCheck });
      }
    }
    return out;
  }, [board, selected, legalSet, lastMove, checkSquare]);

  const sceneChildren = [
    h(CameraOrient, { key: "cam", flipped }),
    h(OrbitControls, {
      key: "orbit",
      enableDamping: true,
      dampingFactor: 0.08,
      enablePan: false,
      minDistance: 6,
      maxDistance: 16,
      minPolarAngle: Math.PI * 0.18,
      maxPolarAngle: Math.PI * 0.46,
    }),
    h(Lights, { key: "lights" }),
    h(Suspense, { key: "scene", fallback: null },
      h("group", { rotation: [0, flipped ? Math.PI : 0, 0] },
        h(BoardFrame, { key: "frame" }),
        ...cells.map((c) =>
          h(BoardSquare, {
            key: c.sq,
            file: c.file,
            rank: c.rank,
            isLight: c.isLight,
            isSelected: c.isSelected,
            isLegal: c.isLegal,
            isLastMove: c.isLastMove,
            isCheck: c.isCheck,
            onClick: () => onSquareClick(c.sq),
          }),
        ),
        ...cells
          .filter((c) => c.piece)
          .map((c) => {
            const { x, z } = squareToWorld(c.file, c.rank);
            return h(ChessPiece, {
              key: `p-${c.sq}`,
              piece: c.piece,
              x, z,
              onClick: () => onSquareClick(c.sq),
            });
          }),
      ),
    ),
  ];

  return (
    <div
      data-testid="chess-board-3d"
      style={{
        width: "100%",
        maxWidth: 720,
        height,
        background: "linear-gradient(180deg,#0F0E0C 0%,#191613 100%)",
        border: `1px solid ${PAL.rimGold}`,
        boxShadow: "inset 0 0 0 1px rgba(200,164,90,0.18), 0 8px 30px rgba(0,0,0,0.55)",
      }}
    >
      {h(Canvas,
        {
          shadows: true,
          dpr: [1, 2],
          camera: { position: [0, 7, 8], fov: 42 },
          onPointerMissed: () => onBackdropClick && onBackdropClick(),
        },
        ...sceneChildren,
      )}
    </div>
  );
}
